import { Box, Stack, Typography } from "@mui/material";
import { useState, cloneElement, ReactElement, useEffect } from "react";
import TextButton from "@/components/common/buttons/textButton";
import InfoCard from "@/components/common/infoCard/infoCard";
import RadioList, { RadioItem } from "@/components/common/list/radioList";
import SlideBottomDialog from "@/components/common/modals/slideBottomDialog";
import { GREY, TEXT_SECONDARY } from "@/config/mui/colorPalette";

type SelectItem = {
  id: string;
  label: string;
};

type SelectProps = {
  inputNode: ReactElement<{ onClick?: () => void }>;
  selectListTitle: string;
  selectListSubmitLabel?: string;
  items: SelectItem[];
  selectedValue: string | null;
  onSubmit: (id: string) => void;
};

function Select({
  inputNode,
  selectListTitle,
  selectListSubmitLabel,
  items,
  selectedValue,
  onSubmit,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(
    selectedValue
  );

  useEffect(() => {
    setSelectedItemId(selectedValue);
  }, [selectedValue]);

  const radioItems: RadioItem[] = items.map((item) => ({
    id: item.id,
    label: item.label,
    onChange: (id) => setSelectedItemId(id),
    isChecked: item.id === selectedItemId,
  }));

  return (
    <>
      {cloneElement(inputNode, { onClick: () => setIsOpen(true) })}

      <SlideBottomDialog
        open={isOpen}
        title={selectListTitle}
        submitButtonText={selectListSubmitLabel}
        onClose={() => {
          setSelectedItemId(selectedValue);
          setIsOpen(false);
        }}
        onSubmit={() => {
          onSubmit(selectedItemId);
          setIsOpen(false);
        }}
      >
        <RadioList items={radioItems} withDivider itemSx={{ py: 0 }} />
      </SlideBottomDialog>
    </>
  );
}

type ButtonInputProps = {
  label: string;
  value: string;
  onClick?: () => void;
};

export function ButtonInput({ label, value, onClick }: ButtonInputProps) {
  return (
    <InfoCard
      sx={{
        bgcolor: GREY[15],
        ".MuiCardContent-root": { pt: 1, pb: "8px !important" },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack direction="row" spacing={1}>
          <Typography
            sx={{
              color: TEXT_SECONDARY,
            }}
          >
            {label}:
          </Typography>{" "}
          <Typography variant="subtitleDefault">{value}</Typography>
        </Stack>

        <Box>
          <TextButton onClick={onClick}>Change</TextButton>
        </Box>
      </Stack>
    </InfoCard>
  );
}

export default Select;
